<template>
	<NuxtLink class="insight-wrapper" :to="computedSlug" :style="{ '--insight-mask-color': maskColor, '--insight-media-clip-path': `url(#${mediaClipPathId})` }">
		<div class="image-wrapper">
			<svg class="svg-mask" viewBox="0 0 368 270" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
				<defs>
					<clipPath :id="mediaClipPathId" clipPathUnits="objectBoundingBox">
						<path
							d="M1 0.983348 C1 0.99153 0.995133 0.998163 0.98913 0.998163 H0.922475 C0.868851 0.998163 0.817201 0.974285 0.780786 0.932415 C0.74437 0.890541 0.692717 0.866667 0.638549 0.866667 H0.01087 C0.004866 0.866667 0 0.860033 0 0.851852 V0.014815 C0 0.006633 0.004866 0 0.01087 0 H0.98913 C0.995133 0 1 0.006633 1 0.014815 V0.983348 Z"
						/>
					</clipPath>
				</defs>
			</svg>
			<div class="media-mask">
				<span class="gradient" />
				<MediaAsset v-if="media" class="media" :media="media" />
				<nuxt-img v-else class="desktop" src="/imgs/temp/insight.jpg" alt="desktop header graphic" />
			</div>
		</div>

		<div class="text-content">
			<div class="-label-regular upper-title">{{ label }}</div>
			<div class="insight-details">
				<div v-if="hasSpecs" class="category-date -label-regular">
					<span v-if="primarySpec" class="read-time">{{ primarySpec }}</span>
					<span v-if="primarySpec && formattedDate" class="divider">/</span>
					<span v-if="formattedDate" class="date">{{ formattedDate }}</span>
				</div>
				<h4 class="-title-5 title">{{ insightTitle }}</h4>
			</div>
		</div>
	</NuxtLink>
</template>

<script setup>
import { MediaAsset } from '~/components/core'

const props = defineProps({
	metadata: { type: Object, default: undefined },
	articleSetup: { type: Object, default: undefined },
	presentation: { type: Object, default: undefined },
	readTime: { type: [Number, String], default: undefined },
	label: { type: String, default: 'Featured ⬎' },
	maskColor: { type: String, default: 'var(--color-geral-white)' },
})

const ALL_CATEGORY = 'all'
const route = useRoute()

const getFirstQueryValue = (value) => (Array.isArray(value) ? value[0] : value)

const normalizeSlug = (value) => {
	if (!value) return ''
	return String(value)
		.trim()
		.replace(/^\/+|\/+$/g, '')
		.toLowerCase()
}

const categorySlug = computed(() => normalizeSlug(props.articleSetup?.categorySlug?.current || props.articleSetup?.categorySlug))
const articleSlug = computed(() => normalizeSlug(props.metadata?.slug?.current || props.metadata?.slug))
const mediaClipPathId = computed(() => `insight-wrapper-image-clip-path-${articleSlug.value || 'default'}`)
const previousCategory = computed(() => {
	if (route.path !== '/insights') return ''

	const category = normalizeSlug(getFirstQueryValue(route.query?.category))
	if (!category || category === ALL_CATEGORY) return ''
	return category
})

const computedSlug = computed(() => {
	if (!categorySlug.value || !articleSlug.value) return '/insights'

	const path = `/insights/${categorySlug.value}/${articleSlug.value}`
	if (!previousCategory.value) return path

	return {
		path,
		query: {
			category: previousCategory.value,
		},
	}
})

const media = computed(() => props.presentation?.media ?? null)

const insightTitle = computed(() => props.metadata?.title || '')
const category = computed(() => props.articleSetup?.category || '')

const readTimeLabel = computed(() => {
	const readTime = Number(props.readTime)
	if (!Number.isFinite(readTime) || readTime <= 0) return ''
	return `${Math.round(readTime)} min read`
})

const primarySpec = computed(() => readTimeLabel.value || category.value)

const formattedDate = computed(() => {
	const value = props.articleSetup?.date
	if (!value) return ''

	const date = new Date(value)
	if (Number.isNaN(date.getTime())) return value

	return date.toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	})
})

const hasSpecs = computed(() => !!(primarySpec.value || formattedDate.value))
</script>

<style lang="scss" scoped>
.insight-wrapper {
	position: relative;
	display: block;
	height: rs(600, 535);

	color: inherit;
	text-decoration: none;

	@include min-width(1800px) {
		max-height: 700px;
	}

	@include max-width($sm) {
		height: rs(400, 342);
	}

	.image-wrapper {
		position: relative;
		width: 100%;
		height: rs(600, 535);
		max-height: rs(600, 535);

		@include max-width($sm) {
			height: 100%;
			max-height: 100%;
		}

		.media-mask {
			position: relative;
			z-index: 1;
			width: 100%;
			height: rs(600, 535);
			min-height: rs(600, 535);
			display: block;
			-webkit-clip-path: var(--insight-media-clip-path);
			clip-path: var(--insight-media-clip-path);

			@include max-width($sm) {
				height: 100%;
				min-height: 100%;
			}
		}

		.media-mask :deep([data-component='media-asset']),
		.media-mask :deep([data-component='image-asset']),
		.media-mask :deep([data-component='video-asset']) {
			display: block;
			width: 100%;
			height: 100%;
		}

		.media-mask :deep(img),
		.media-mask :deep(video) {
			display: block;
			width: 100%;
			height: 100%;
			object-fit: cover;
		}
	}

	.desktop {
		position: relative;
		z-index: 1;
		width: 100%;
		height: 100%;
		display: block;
		object-fit: cover;
	}

	.media {
		position: relative;
		z-index: 1;
		width: 100%;
		height: 100%;
		display: block;
		object-fit: cover;
	}

	.gradient {
		position: absolute;
		inset: 0;
		bottom: 0;
		top: inherit;
		width: 100%;
		height: 50%;
		z-index: 2;
		-webkit-clip-path: var(--insight-media-clip-path);
		clip-path: var(--insight-media-clip-path);

		background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.7) 57.02%);

		@include max-width($xs) {
			height: 70%;
		}
	}

	.svg-mask {
		position: absolute;
		width: 0;
		height: 0;
		overflow: hidden;
		pointer-events: none;
	}

	.text-content {
		position: absolute;
		z-index: 3;
		inset: 0;
		width: 100%;
		height: 100%;

		display: flex;
		flex-direction: column;
		justify-content: space-between;

		padding: rs(32, 24);
		padding-bottom: rs(120, 80);

		@include min-width(1800px) {
			padding-bottom: lrs(110) !important;
		}

		.upper-title {
			color: var(--color-geral-white);
		}

		.insight-details {
			width: calc(column-width(6) - #{rs(32, 32)});

			@include max-width($sm) {
				width: 100%;
			}

			.category-date {
				display: flex;
				flex-direction: row;
				gap: rs(12, 12);
				margin-bottom: rs(12, 12);

				.read-time {
					color: var(--color-geral-white);
					@include inherit-font;
				}

				.divider {
					color: var(--color-geral-white);
					@include inherit-font;
				}

				.date {
					color: var(--color-geral-white);
					@include inherit-font;
				}
			}

			.title {
				color: var(--color-geral-white);
			}
		}
	}
}
</style>
